/**
 * ResolverPreviewSection — previews which policy a piece of work runs under: pick a kind of work and optionally a
 * carried policy; the server returns the carried policy when it is allowed for that kind of work, else the default.
 * Template example: demo `/form-layouts` (request card) + NextAdmin alerts for the result.
 */
import { Target } from 'lucide-react';
import type React from 'react';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { resolveModelRuntimePolicyMutation } from '../../../generated/api/@tanstack/react-query.gen.js';
import { RuntimePolicySurface } from '../../../generated/api/types.gen.js';
import { zModelRuntimePolicyResolveRequest } from '../../../generated/api/zod.gen.js';
import { Form, FormActions, FormSection, SelectFormField, useZodForm } from '../../../components/form/index.js';
import { Callout, Card, Skeleton } from '../../../components/ui/index.js';
import { normalizeApiError } from '../../../services/api/errors.js';
import { runtimePoliciesQuery } from '../../../services/api/runtime-policy.queries.js';
import { enumOptions } from './types.js';

const SURFACES = Object.values(RuntimePolicySurface);
const DEFAULT_OPTION = 'default';

/** The API's resolve request, with the optional policy as a select value ("default" = none carried). */
const resolveFormSchema = zModelRuntimePolicyResolveRequest
  .omit({ policyId: true })
  .extend({ policyChoice: z.string() });

export const ResolverPreviewSection: React.FC = () => {
  const policiesQuery = useQuery(runtimePoliciesQuery());
  const resolve = useMutation(resolveModelRuntimePolicyMutation());
  const form = useZodForm(resolveFormSchema, {
    defaultValues: { surface: RuntimePolicySurface.CONVERSATION, policyChoice: DEFAULT_OPTION },
  });

  const policyOptions = [
    // The policies could not be read: only the kind of work's default can be previewed.
    {
      value: DEFAULT_OPTION,
      label: policiesQuery.isError ? 'Kind-of-work default (policies unavailable)' : 'Kind-of-work default',
    },
    ...(policiesQuery.data ?? []).map((p) => ({ value: String(p.id), label: p.name })),
  ];

  const resolved = resolve.data?.data ?? null;

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <Form
          form={form}
          onSubmit={({ surface, policyChoice }) => {
            resolve.mutate({
              body: { surface, ...(policyChoice !== DEFAULT_OPTION ? { policyId: Number(policyChoice) } : {}) },
            });
          }}
        >
          <FormSection title="Resolve preview" description="See which policy a piece of work would run under.">
            <SelectFormField name="surface" label="Kind of work" options={enumOptions(SURFACES)} />
            <SelectFormField name="policyChoice" label="Carried policy" options={policyOptions} />
          </FormSection>
          <FormActions submitLabel={resolve.isPending ? 'Resolving…' : 'Resolve'} />
        </Form>
      </Card>

      <Card title="Resolved policy">
        {resolve.isPending && (
          <div className="space-y-3">
            <Skeleton height={14} width="70%" />
            <Skeleton height={12} width="50%" />
          </div>
        )}
        {!resolve.isPending && !resolve.isError && !resolved && (
          <p className="flex items-center gap-2 text-sm text-muted">
            <Target size={14} aria-hidden="true" />
            Run a resolve request to preview policy selection.
          </p>
        )}
        {!resolve.isPending && resolved && (
          <Callout tone="accent" title={resolved.name}>
            {resolved.models.length > 0
              ? resolved.models.map((m) => m.displayName).join(' → ')
              : 'No models in this policy.'}
          </Callout>
        )}
        {!resolve.isPending && resolve.isError && (
          <Callout tone="warning" title="Not allowed">
            {normalizeApiError(resolve.error, 0).message}
          </Callout>
        )}
      </Card>
    </div>
  );
};
